import {FormattedMessage, defineMessages} from "react-intl";
import {Divider, Typography} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {ReactNode} from "react";

/// the error codes we have translations for
const supportedErrorCodes = [401, 403, 404] as const;
export type ErrorCode = (typeof supportedErrorCodes)[number];

export function parseErrorCode(input: number | string): ErrorCode {
    const numeric = typeof input === "string" ? parseInt(input) : input;
    const code = supportedErrorCodes.find((valid) => valid === numeric);
    if (code) return code;
    else return 404;
}

const supportedErrorReasons = [
    "no_roles",
    "unknown_domain",
    "unknown_endpoint",
    "unknown_location",
    "missing_permission",
    "unexpected",
] as const;
/// the error reasons we have translations for
export type ErrorReason = (typeof supportedErrorReasons)[number];

const ERROR_CODE_MESSAGES = defineMessages<ErrorCode>({
    401: {id: "errors.codes.401", defaultMessage: "Unauthorized"},
    403: {id: "errors.codes.403", defaultMessage: "You dont have enough permission to see that page."},
    404: {id: "errors.codes.404", defaultMessage: "We can't find the page you're looking for."},
});

export const ERROR_REASON_MESSAGES = defineMessages<ErrorReason>({
    no_roles: {id: "errors.reasons.no_roles", defaultMessage: "There are no roles assigned to this user."},
    unknown_domain: {id: "errors.reasons.unknown_domain", defaultMessage: "This domain does not exists!"},
    unknown_endpoint: {id: "errors.reasons.unknown_endpoint", defaultMessage: "This endpoint does not exists!"},
    unknown_location: {id: "errors.reasons.unknown_location", defaultMessage: "This location does not exists!"},
    missing_permission: {
        id: "errors.reasons.missing_permission",
        defaultMessage: "The user does not have the required permission.",
    },
    unexpected: {id: "errors.reasons.unexpected", defaultMessage: "Sorry, an unexpected error has occurred."},
});

export function parseErrorReason(input: string): ErrorReason {
    const code = supportedErrorReasons.find((valid) => valid === input);
    if (code) return code;
    else return "unexpected";
}

type SectionTitle = "error_page.reason" | "error_page.url";

const SECTION_TITLE_MESSAGES = defineMessages<SectionTitle>({
    "error_page.reason": {id: "error_page.reason", defaultMessage: "Reason"},
    "error_page.url": {id: "error_page.url", defaultMessage: "Original url"},
});

const Section = (props: {icon: IconProp; title: SectionTitle; children: ReactNode}) => (
    <div style={{display: "flex", flexDirection: "row", alignItems: "center", marginTop: 10, marginBottom: 10}}>
        <div style={{marginRight: 10}}>
            <FontAwesomeIcon size="2x" icon={props.icon} />
        </div>
        <div>
            <Typography variant="subtitle1">
                <FormattedMessage {...SECTION_TITLE_MESSAGES[props.title]} />
            </Typography>
            <Typography variant="subtitle2" style={{opacity: 0.8}}>
                {props.children}
            </Typography>
        </div>
    </div>
);

export default function ErrorDisplay({
    code,
    reason,
    path = null,
}: {
    code: ErrorCode;
    reason: ErrorReason;
    path?: string;
}) {
    return (
        <div style={{margin: 20}}>
            <Typography variant="h2">
                <FormattedMessage id="error_page.title" defaultMessage="Oops!" />
            </Typography>
            <Typography variant="subtitle1">
                <FormattedMessage {...ERROR_CODE_MESSAGES[code.toString() as `${ErrorCode}`]} />
            </Typography>

            <Divider style={{flexGrow: 1, marginTop: 10, marginBottom: 10}} />

            <Section icon="question-circle" title="error_page.reason">
                <FormattedMessage {...ERROR_REASON_MESSAGES[reason]} />
            </Section>

            {path != null && (
                <Section icon="folder" title="error_page.url">
                    {window.location.origin}
                    {path}
                </Section>
            )}
        </div>
    );
}
